import React, { useState, useEffect } from "react";
import logo from "../../assets/logo.png";
import { Link, useLocation } from "react-router-dom";
import { CiUser, CiShoppingCart, CiSearch } from "react-icons/ci";
import { HiMenuAlt3, HiX } from "react-icons/hi";
import { useSelector } from "react-redux";
import useWishlist from "../../customhooks/useWishlist";
import SearchBox from "./SearchBox";

const BASE_API = (process.env.REACT_APP_BASE_API || "").replace(/\/+$/, "");
const UPLOAD_PATH = "uploads"; // same folder your product images use

const toImageUrl = (src) => {
  if (!src) return null;
  if (/^(https?:)?\/\//.test(src) || src.startsWith("data:")) return src;
  const clean = String(src).replace(/^\/+/, "");
  return `${BASE_API}/${clean.includes("/") ? clean : `${UPLOAD_PATH}/${clean}`}`;
};

/* Shown until the saved content loads, and used if the API fails */
const DEFAULT_LINKS = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "About US", to: "/about" },
  { label: "Contact", to: "/contact-us" },
];

function Header() {
  const [open, setOpen] = useState(false); // mobile menu
  const [searchOpen, setSearchOpen] = useState(false); // mobile search bar
  const [navLinks, setNavLinks] = useState(DEFAULT_LINKS);
  const [logoSrc, setLogoSrc] = useState(logo);
  const location = useLocation();

  /* Load the header content saved from the admin panel */
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${BASE_API}/headerinfo`, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        if (Array.isArray(d.navLinks) && d.navLinks.length) setNavLinks(d.navLinks);
        if (d.logo) setLogoSrc(toImageUrl(d.logo));
      })
      .catch(() => {}); // keep the defaults
    return () => controller.abort();
  }, []);

  useEffect(() => {
    setOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const count = useSelector((state) =>
    state.cart.items.reduce((sum, i) => sum + i.qty, 0),
  );

  // the heart icon is not in the new design, but this keeps the wishlist syncing
  useWishlist({ sync: true });

  return (
    <header className="w-full fixed top-0 left-0 z-50 bg-white shadow-sm">
      <div className="h-[72px] w-full max-w-[1240px] mx-auto px-4 flex items-center gap-3 sm:gap-6">
        {/* Logo */}
        <Link to="/" className="shrink-0 h-full flex items-center py-2">
          <img
            className="h-[56px] sm:h-[84px] w-auto object-contain origin-left"
            src={logoSrc}
            alt="Jeetayu"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = logo;
            }}
          />
        </Link>

        {/* Page links (wide screens only). Delete this block to match the design exactly. */}
        {/* <nav className="hidden xl:flex items-center gap-5 uppercase shrink-0" aria-label="Main">
          {navLinks.map((item, i) => (
            <Link
              key={`${item.to}-${i}`}
              to={item.to}
              className="text-[13px] font-[500] text-gray-700 hover:text-[#2f6b3a] transition-colors"
            >
              {item.label}
            </Link>
          ))}
        </nav> */}

        {/* Search bar */}
        <div className="hidden md:block flex-1 min-w-0 max-w-[640px] mx-auto">
          <SearchBox />
        </div>

        {/* Icons */}
        <div className="flex items-center gap-3 sm:gap-5 ml-auto md:ml-0 shrink-0">
          <button
            type="button"
            className="md:hidden p-1"
            onClick={() => {
              setSearchOpen((v) => !v);
              setOpen(false);
            }}
            aria-label={searchOpen ? "Close search" : "Open search"}
            aria-expanded={searchOpen}
          >
            {searchOpen ? <HiX className="text-[24px]" /> : <CiSearch className="text-[26px]" />}
          </button>

          <Link to="/myaccount" aria-label="My account">
            <CiUser className="text-[26px] sm:text-[28px]" />
          </Link>

          <Link to="/cart" className="relative" aria-label="Cart">
            <CiShoppingCart className="text-[26px] sm:text-[28px]" />
            {count > 0 && (
              <span className="absolute -top-1 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#1f4a2b] text-white text-[11px] font-[600]">
                {count > 99 ? "99+" : count}
              </span>
            )}
          </Link>

          <button
            type="button"
            className="xl:hidden p-1"
            onClick={() => {
              setOpen((v) => !v);
              setSearchOpen(false);
            }}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <HiX className="text-[26px]" /> : <HiMenuAlt3 className="text-[26px]" />}
          </button>
        </div>
      </div>

      {/* Mobile search panel */}
      {searchOpen && (
        <div className="md:hidden absolute left-0 top-full w-full bg-white border-t border-gray-100 shadow-md p-3">
          <SearchBox autoFocus onDone={() => setSearchOpen(false)} />
        </div>
      )}

      {/* Mobile / tablet menu */}
      <div
        className={`xl:hidden absolute left-0 top-full w-full bg-white border-t border-gray-100 shadow-md overflow-y-auto transition-all duration-300 ease-in-out ${
          open ? "max-h-[calc(100vh-72px)] opacity-100" : "max-h-0 opacity-0 pointer-events-none"
        }`}
      >
        <div className="flex flex-col px-4 py-2 uppercase">
          {navLinks.map((item, i) => (
            <Link
              key={`${item.to}-${i}`}
              to={item.to}
              className="py-3 text-[15px] font-[500] border-b border-gray-100 last:border-b-0"
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </header>
  );
}

export default Header;
